import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Building2, Link2 } from 'lucide-react';
import { Button, Input, Modal, SegmentedControl, Select, Skeleton, Textarea } from '../../../components/ui';
import { ApiError } from '../../../lib/api';
import { EMPTY_BILLING, type ClientBilling } from '../api';
import { useAccountsOrgMembers, type ClientImportRow } from '../../directory/api';

export interface ClientFormValues {
  name: string;
  notes: string;
  /** Bill-to block — printed verbatim on this client's invoices. */
  billing: ClientBilling;
  /**
   * The Accounts organization this client is being made FROM, when the form
   * was opened with an Accounts picker and one was chosen. The page imports
   * it (linking an existing client of the same name rather than creating a
   * second one) and applies the bill-to details on top.
   */
  accountsOrg?: ClientImportRow | null;
}

/** What the New-client form knows about Accounts — supplied only on CREATE. */
export interface AccountsPicker {
  rows: ClientImportRow[];
  loading: boolean;
  /** Why Accounts cannot be offered right now (directory off, unreachable). */
  unavailable: string | null;
}

type Source = 'accounts' | 'manual';

/** Blank strings in, nulls out: the API treats '' and null the same, but the
 *  form needs strings and the wire prefers explicit nulls. */
function toStrings(billing: ClientBilling | undefined): Record<keyof ClientBilling, string> {
  const source = billing ?? EMPTY_BILLING;
  return Object.fromEntries(
    (Object.keys(EMPTY_BILLING) as Array<keyof ClientBilling>).map((k) => [k, source[k] ?? '']),
  ) as Record<keyof ClientBilling, string>;
}

export function ClientFormModal({
  open,
  onOpenChange,
  title,
  submitLabel,
  initial,
  onSubmit,
  submitting,
  accounts,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  submitLabel: string;
  initial?: ClientFormValues;
  onSubmit: (values: ClientFormValues) => Promise<unknown>;
  submitting: boolean;
  /**
   * Offer "From Accounts" beside "Manual". Jesse, 2026-09-10: *"when clicking
   * new client you have the option of selecting an accounts org or manual"*.
   * Omit on Edit — an existing client's link is managed on its own page.
   */
  accounts?: AccountsPicker;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [billing, setBilling] = useState(() => toStrings(initial?.billing));
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<Source>('manual');
  const [orgFilter, setOrgFilter] = useState('');
  const [accountsOrg, setAccountsOrg] = useState<ClientImportRow | null>(null);

  const offered = useMemo(
    () => (accounts?.rows ?? []).filter((row) => row.action !== 'linked'),
    [accounts?.rows],
  );
  const accountsUsable = Boolean(accounts) && !accounts?.unavailable;

  // Reset the form each time the modal opens. Accounts is the default source
  // whenever it can be offered: the company usually already exists there, and
  // typing a name Accounts holds is how one client becomes two.
  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setNotes(initial?.notes ?? '');
      setBilling(toStrings(initial?.billing));
      setError(null);
      setOrgFilter('');
      setAccountsOrg(null);
      setSource(accountsUsable ? 'accounts' : 'manual');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountsUsable]);

  const setField = (field: keyof ClientBilling, value: string) =>
    setBilling((b) => ({ ...b, [field]: value }));

  // Who at that organization gets the invoice — its people, read live from
  // Accounts once an org is chosen (Jesse: "select the user account we want to
  // put as billing, or enter an email manually"). '' = typed by hand.
  const [contactPick, setContactPick] = useState('');
  const members = useAccountsOrgMembers(source === 'accounts' && accountsUsable ? (accountsOrg?.orgId ?? null) : null);

  const chooseOrg = (row: ClientImportRow) => {
    setAccountsOrg(row);
    setName(row.name);
    setContactPick('');
    // Accounts' headquarters seeds the bill-to block; nothing already typed is overwritten.
    setBilling((b) => ({
      ...b,
      addressLine1: b.addressLine1 || row.address || '',
      city: b.city || row.city || '',
      region: b.region || row.region || '',
      postalCode: b.postalCode || row.postalCode || '',
    }));
    setError(null);
  };

  const pickContact = (userId: string) => {
    setContactPick(userId);
    const person = members.data?.members.find((m) => m.userId === userId);
    if (!person) return; // "Enter manually" — leave whatever is typed
    setBilling((b) => ({ ...b, billingContactName: person.name, billingEmail: person.email }));
  };

  const visibleOrgs = useMemo(() => {
    const needle = orgFilter.trim().toLowerCase();
    return needle ? offered.filter((row) => row.name.toLowerCase().includes(needle)) : offered;
  }, [offered, orgFilter]);

  const fromAccounts = source === 'accounts' && accountsUsable;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (fromAccounts && !accountsOrg) {
      setError('Pick the Accounts organization, or switch to Manual.');
      return;
    }
    if (!name.trim()) {
      setError('Client name is required');
      return;
    }
    setError(null);
    try {
      await onSubmit({
        name: name.trim(),
        notes: notes.trim(),
        billing: (Object.keys(EMPTY_BILLING) as Array<keyof ClientBilling>).reduce(
          (acc, k) => {
            acc[k] = billing[k].trim() || null;
            return acc;
          },
          { ...EMPTY_BILLING },
        ),
        accountsOrg: fromAccounts ? accountsOrg : null,
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Something went wrong — please try again.');
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {accounts && (
          <div className="space-y-3">
            <SegmentedControl<Source>
              ariaLabel="Where the client comes from"
              size="sm"
              value={fromAccounts ? 'accounts' : 'manual'}
              onChange={(value) => {
                setSource(value);
                setError(null);
                if (value === 'manual') setAccountsOrg(null);
              }}
              options={[
                { value: 'accounts', label: 'From Accounts' },
                { value: 'manual', label: 'Manual' },
              ]}
            />
            {source === 'accounts' && accounts.unavailable && (
              <p className="text-body-sm text-grey-600">
                Accounts is not available right now — {accounts.unavailable}. Switch to Manual.
              </p>
            )}
            {fromAccounts && (
              <div className="rounded-xl border border-grey-200 p-3">
                {accounts.loading ? (
                  <Skeleton className="h-24" />
                ) : offered.length === 0 ? (
                  <p className="text-body-sm text-grey-600">
                    Every client organization in Accounts is already a client here. Switch to Manual to add one
                    that is not in Accounts.
                  </p>
                ) : (
                  <>
                    <Input
                      placeholder={`Find among ${offered.length} organizations…`}
                      value={orgFilter}
                      onChange={(e) => setOrgFilter(e.target.value)}
                      autoFocus
                      aria-label="Filter Accounts organizations"
                    />
                    <ul className="mt-2 max-h-56 divide-y divide-grey-100 overflow-y-auto" role="listbox">
                      {visibleOrgs.map((row) => {
                        const active = accountsOrg?.orgId === row.orgId;
                        return (
                          <li key={row.orgId}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={active}
                              onClick={() => chooseOrg(row)}
                              className={`flex w-full items-center gap-3 px-2 py-2 text-left text-body-sm transition-colors ${
                                active ? 'bg-secondary text-white' : 'hover:bg-grey-50'
                              }`}
                            >
                              <Building2 className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                              <span className="min-w-0 flex-1 truncate font-medium">{row.name}</span>
                              {row.action === 'link-by-name' && (
                                <span
                                  className={`inline-flex shrink-0 items-center gap-1 text-xs ${
                                    active ? 'text-white/80' : 'text-grey-500'
                                  }`}
                                >
                                  <Link2 className="h-3.5 w-3.5" aria-hidden />
                                  links existing “{row.clientName}”
                                </span>
                              )}
                              {[row.city, row.region].filter(Boolean).length > 0 && (
                                <span className={`shrink-0 text-xs ${active ? 'text-white/80' : 'text-grey-500'}`}>
                                  {[row.city, row.region].filter(Boolean).join(', ')}
                                </span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                      {visibleOrgs.length === 0 && (
                        <li className="px-2 py-3 text-body-sm text-grey-500">Nothing matches “{orgFilter}”.</li>
                      )}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <Input
          label="Client name"
          placeholder="e.g. Meridian Health Partners"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={error ?? undefined}
          autoFocus={!accounts}
          maxLength={200}
          readOnly={fromAccounts}
          hint={fromAccounts ? 'The name comes from Accounts — change it there.' : undefined}
        />

        {/* Bill-to details: this is exactly what prints in the "Bill to" block
            of every invoice for this client. Without it the invoice you hand a
            client carries nothing but their company name. */}
        <fieldset className="rounded-xl border border-grey-200 p-4">
          <legend className="px-1.5 text-caption font-bold uppercase tracking-wider text-grey-500">
            Bill to — printed on invoices
          </legend>
          <div className="grid gap-4 sm:grid-cols-2">
            {fromAccounts && accountsOrg && (
              <Select
                label="Billing contact from Accounts"
                className="sm:col-span-2"
                value={contactPick}
                onChange={(e) => pickContact(e.target.value)}
                disabled={members.isLoading}
                options={[
                  { value: '', label: 'Enter manually' },
                  ...(members.data?.members ?? []).map((m) => ({
                    value: m.userId,
                    label: `${m.name} · ${m.email}`,
                  })),
                ]}
              />
            )}
            {fromAccounts && accountsOrg && (
              <p className="-mt-2 text-caption text-grey-500 sm:col-span-2">
                {members.isLoading
                  ? 'Reading their people from Accounts…'
                  : members.data && !members.data.ok
                    ? `Their people could not be read — ${members.data.reason ?? 'directory unavailable'}. Enter the contact by hand.`
                    : members.data && members.data.members.length === 0
                      ? 'Nobody is a member of that organization in Accounts yet — enter the contact by hand.'
                      : 'Picking a person fills the contact name and email below; you can still edit them.'}
              </p>
            )}
            <Input
              label="Billing contact"
              placeholder="Dana Whitcomb"
              value={billing.billingContactName}
              onChange={(e) => setField('billingContactName', e.target.value)}
              maxLength={200}
            />
            <Input
              label="Billing email"
              type="email"
              placeholder="ap@client.example"
              value={billing.billingEmail}
              onChange={(e) => setField('billingEmail', e.target.value)}
              maxLength={200}
              hint="Where payment reminders are addressed"
            />
            <Input
              label="Address line 1"
              placeholder="4820 N Winchester Ave"
              value={billing.addressLine1}
              onChange={(e) => setField('addressLine1', e.target.value)}
              maxLength={200}
              className="sm:col-span-2"
            />
            <Input
              label="Address line 2"
              placeholder="Suite 310"
              value={billing.addressLine2}
              onChange={(e) => setField('addressLine2', e.target.value)}
              maxLength={200}
              className="sm:col-span-2"
            />
            <Input
              label="City"
              placeholder="Chicago"
              value={billing.city}
              onChange={(e) => setField('city', e.target.value)}
              maxLength={200}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="State / region"
                placeholder="IL"
                value={billing.region}
                onChange={(e) => setField('region', e.target.value)}
                maxLength={200}
              />
              <Input
                label="Postal code"
                placeholder="60640"
                value={billing.postalCode}
                onChange={(e) => setField('postalCode', e.target.value)}
                maxLength={200}
              />
            </div>
            <Input
              label="Country"
              placeholder="United States"
              value={billing.country}
              onChange={(e) => setField('country', e.target.value)}
              maxLength={200}
            />
            <Input
              label="Tax ID"
              placeholder="EIN 36-4471902"
              value={billing.taxId}
              onChange={(e) => setField('taxId', e.target.value)}
              maxLength={200}
            />
          </div>
        </fieldset>

        <Textarea
          label={
            <>
              Notes <span className="font-normal text-grey-400">(optional)</span>
            </>
          }
          textareaClassName="min-h-[80px]"
          placeholder="Terms, context, who to chase…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={5000}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {fromAccounts && accountsOrg?.action === 'link-by-name' ? `Link “${accountsOrg.clientName}”` : submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
